import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, NgForm, NG_VALUE_ACCESSOR, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';

import { EditWebcastService } from 'rev-portal/scheduledEvents/editWebcast/EditWebcast.Service';
import { BaseControlValueAccessor } from 'rev-shared/util/BaseControlValueAccessor';
import { toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';

import { EmbeddedContentIconList, EmbeddedContentIcon, IEmbeddedContentLink } from './EmbeddedContentTypes';

@Component({
	selector: 'embedded-content-add-form',
	templateUrl: './EmbeddedContentAddForm.Component.html',
	standalone: false,
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => EmbeddedContentAddFormComponent),
		multi: true
	}, {
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => EmbeddedContentAddFormComponent),
		multi: true
	}],
	styleUrls: ['./EmbeddedContentAddForm.ng.less']
})
export class EmbeddedContentAddFormComponent extends BaseControlValueAccessor<IEmbeddedContentLink> implements OnInit, AfterViewInit, OnDestroy, Validator {
	@Input() public webcastId: string;
	@Input() public isDuplicateName: (linkName: string) => boolean = () => false;
	@Input() public isSidebar: boolean;

	@Output() public saveEntry: EventEmitter<IEmbeddedContentLink> = new EventEmitter();
	@Output() public cancel: EventEmitter<void> = new EventEmitter();

	@ViewChild(NgForm, { static: true }) public form!: NgForm;

	public readonly iconList: EmbeddedContentIcon[] = EmbeddedContentIconList;
	public isCodeValidating = false;
	public isEditMode = false;

	private validateFn?: () => void;
	private readonly subscription = new Subscription();

	constructor(
		private readonly EditWebcastService: EditWebcastService
	) {
		super();
	}

	public override writeValue(val: IEmbeddedContentLink): void {
		super.writeValue(val);
		this.initializeValue();
	}

	public ngOnInit(): void {
		this.initializeValue();
	}

	private initializeValue(): void {
		if (!this.value) {
			this.value = { name: '', code: '', icon: '' };
		}
		this.isEditMode = !!this.value?.name?.trim();
	}

	public ngAfterViewInit(): void {
		if (this.form?.statusChanges) {
			this.subscription.add(this.form.statusChanges.subscribe(() => this.validateFn?.()));
		}
	}

	public ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public validate(_: AbstractControl): ValidationErrors | null {
		if (this.form?.invalid || this.isCodeValidating) {
			return { invalid: true };
		}

		return null;
	}

	public registerOnValidatorChange(fn: () => void): void {
		this.validateFn = fn;
	}


	public onNameChange(): void {
		toggleError(this.form.controls.eLinkName, 'duplicateName', this.isDuplicateName(this.value.name));
		this.updateModelValue(this.value);
	}

	public validateCode(): void {
		if (!this.form.controls.eLinkCode) {
			return;
		}

		this.isCodeValidating = true;
		this.validateFn?.();

		this.EditWebcastService.validateEmbeddedLinkCode(this.value.code, this.webcastId)
			.then(result => {
				toggleError(this.form.controls.eLinkCode, 'invalidCode', !!result.issue);

				if (result.issue) {
					this.form.controls.eLinkCode.markAsTouched();
					return;
				}

				this.value.resourceUri = result.resourceUri;
				this.updateModelValue(this.value);
			})
			.catch(() => {
				toggleError(this.form.controls.eLinkCode, 'invalidCode', true);
				this.form.controls.eLinkCode.markAsTouched();
			})
			.finally(() => {
				this.isCodeValidating = false;
				this.validateFn?.();
			});
	}

	public onCancelClick(): void {
		this.cancel.emit();
	}

	public onIconChange(): void {
		this.updateModelValue(this.value);
	}

	public onSubmit(): void {
		this.saveEntry.emit(this.value);
	}
}
